import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { CopiesFromProduction, CopyableProject, StagingCopies as Copies, StagingCopyRow } from "../api/types";
import { askToConfirm } from "./confirm";

/** Copies of production projects on staging (backend core/staging_copy.py), part of Admin → Releases.
 * On production: pick projects and send them, and see how staging is getting on. On staging: the copies it holds,
 * to remove when done. */

const size = (n: number) =>
  n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(0)} MB` : n > 0 ? `${Math.max(1, Math.round(n / 1e3))} KB` : "0 KB";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const SHOWN = 15;    // projects listed before searching

function progress(c: StagingCopyRow) {
  if (c.status === "copying") {
    return `Copying: ${c.files_done} of ${c.files_total} files${c.bytes_total ? `, ${size(c.bytes_done)} of ${size(c.bytes_total)}` : ""}…`;
  }
  if (c.status === "stopped") return "Stopped partway (staging restarted): copy it again";
  if (c.status === "failed") return `Didn't finish: ${c.error}`;
  return `✓ Copied${c.files_missing ? ` (${c.files_missing} file${c.files_missing === 1 ? "" : "s"} no longer on production)` : ""}`;
}

function CopyList({ copies, busy, onRemove, onRemoveAll }: {
  copies: StagingCopyRow[]; busy: boolean; onRemove: (c: StagingCopyRow) => void; onRemoveAll: () => void;
}) {
  if (!copies.length) return <div className="muted">No copies on staging.</div>;
  return (
    <>
      <table className="table">
        <tbody>
          {copies.map((c) => (
            <tr key={c.project_id}>
              <td>{c.name}<div className="muted">{c.with_audio ? "with audio" : "without audio"} · {when(c.started_at)} · {c.by}</div></td>
              <td style={{ color: c.status === "done" ? "var(--ok)" : c.status === "copying" ? "var(--muted)" : "var(--bad)" }}>
                {progress(c)}
              </td>
              <td className="r">
                <button className="btn ghost sm" type="button" disabled={busy} onClick={() => onRemove(c)}>Remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 10 }}>
        <button className="btn danger sm" type="button" disabled={busy} onClick={onRemoveAll}>Remove all copies</button>
      </div>
    </>
  );
}

/** On production: the projects, a Copy button each, and staging's copies. */
export function CopyToStaging() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [audio, setAudio] = useState<Record<string, boolean>>({});
  const { data } = useQuery({
    queryKey: ["admin", "releases", "copies"],
    queryFn: () => api<CopiesFromProduction>("/api/admin/releases/copies"),
    refetchInterval: (q) => {
      const st = q.state.data?.staging;
      return st && "copies" in st && st.copies.some((c) => c.status === "copying") ? 5000 : 60000;
    },
  });
  // Fetch afresh after a change: that also restarts the 5-second refresh while a copy is under way.
  const done = () => qc.invalidateQueries({ queryKey: ["admin", "releases", "copies"] });
  const send = useMutation({
    mutationFn: (p: { project_id: string; with_audio: boolean }) =>
      api<{ staging: Copies }>("/api/admin/releases/copies", { method: "POST", body: JSON.stringify(p) }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (projectId?: string) =>
      api<{ staging: Copies }>(`/api/admin/releases/copies${projectId ? `/${projectId}` : ""}`, { method: "DELETE" }),
    onSuccess: done,
  });
  const busy = send.isPending || remove.isPending;

  const copy = async (p: CopyableProject) => {
    const withAudio = !!audio[p.id];
    const what = withAudio && p.audio_bytes ? `with its audio (${size(p.audio_bytes + p.file_bytes)} in all)` : "without its audio";
    if (await askToConfirm(`Copy "${p.name}" to staging ${what}? An earlier copy of it there is replaced.`, { yes: "Copy it" })) {
      send.mutate({ project_id: p.id, with_audio: withAudio });
    }
  };
  const removeOne = async (c: StagingCopyRow) => {
    if (await askToConfirm(`Remove "${c.name}" from staging? Production's project isn't touched.`, { yes: "Remove", danger: true })) {
      remove.mutate(c.project_id);
    }
  };
  const removeAll = async () => {
    if (await askToConfirm("Remove every copy from staging? Production's projects aren't touched.", { yes: "Remove all", danger: true })) {
      remove.mutate(undefined);
    }
  };

  const projects = data?.projects ?? [];
  const term = search.trim().toLowerCase();
  const shown = term ? projects.filter((p) => p.name.toLowerCase().includes(term)) : projects.slice(0, SHOWN);
  const staging = data?.staging;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3>Copy projects to staging</h3>
      <div className="muted" style={{ marginBottom: 10 }}>
        Try a change on real material: a copy keeps everything about the book (reports, pickups, notes, ToC, settings).
        Tick "with audio" to play and proof it there too. Copying a project again replaces its earlier copy. Files
        waiting to be transcribed arrive parked, so nothing is paid for on staging by itself.
      </div>
      <input className="input" placeholder="Search production's projects" value={search}
        onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 360, marginBottom: 8 }} />
      {!data ? (
        <div className="muted">Loading…</div>
      ) : (
        <table className="table">
          <tbody>
            {shown.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td className="muted">{size(p.file_bytes)} of files</td>
                <td>
                  <label className="muted" style={{ whiteSpace: "nowrap" }}>
                    <input type="checkbox" checked={!!audio[p.id]} disabled={!p.audio_bytes}
                      onChange={(e) => setAudio((a) => ({ ...a, [p.id]: e.target.checked }))} />{" "}
                    with audio{p.audio_bytes ? ` (${size(p.audio_bytes)})` : ": none"}
                  </label>
                </td>
                <td className="r">
                  <button className="btn ghost sm" type="button" disabled={busy} onClick={() => copy(p)}>Copy</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!term && projects.length > SHOWN && (
        <div className="muted" style={{ marginTop: 6 }}>The {SHOWN} most recently changed; search for the others.</div>
      )}
      {send.isError && <div className="sw-error">{(send.error as Error).message}</div>}

      <h3 style={{ marginTop: 18 }}>On staging now</h3>
      {staging && "error" in staging ? (
        <div className="muted">Staging can't be asked just now: {staging.error}</div>
      ) : (
        <CopyList copies={staging?.copies ?? []} busy={busy} onRemove={removeOne} onRemoveAll={removeAll} />
      )}
      {remove.isError && <div className="sw-error">{(remove.error as Error).message}</div>}
    </div>
  );
}

/** On staging: the copies it holds from production. */
export function CopiesHere() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin", "staging-copies"],
    queryFn: () => api<Copies>("/api/admin/staging-copies"),
    refetchInterval: (q) => (q.state.data?.copies.some((c) => c.status === "copying") ? 5000 : 60000),
  });
  const remove = useMutation({
    mutationFn: (projectId?: string) =>
      api<Copies>(`/api/admin/staging-copies${projectId ? `/${projectId}` : ""}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "staging-copies"] }),
  });
  if (!data) return null;
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Copies from production</h3>
      <div className="muted" style={{ marginBottom: 10 }}>
        Made from production's Admin → Releases. Removing one here leaves production's project untouched.
      </div>
      <CopyList copies={data.copies} busy={remove.isPending}
        onRemove={async (c) => {
          if (await askToConfirm(`Remove "${c.name}" from staging?`, { yes: "Remove", danger: true })) remove.mutate(c.project_id);
        }}
        onRemoveAll={async () => {
          if (await askToConfirm("Remove every copy from staging?", { yes: "Remove all", danger: true })) remove.mutate(undefined);
        }} />
      {remove.isError && <div className="sw-error">{(remove.error as Error).message}</div>}
    </div>
  );
}
